import { ChevronRight } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { Cap } from '../../primitives'
import { useAgentName } from './agentName'

/** Placeholder content: the shape of the area, not the fleet's real work. */
const TASKS = [
  { label: 'Upload September loss runs', owner: 'Adam', done: true },
  { label: 'Add 4 trucks to the policy', owner: 'Bob', done: false },
  { label: 'Sign the renewal application', owner: 'Jim', done: false },
]

/** What the agent is carrying on its own, between the people's work and their targets. */
const BLUEY_TASKS = [
  { label: 'Actively monitoring claim progress with Geico.', sent: 'October 1, 2026 9:40am' },
  {
    label: 'Actively chasing down updated medical record for Pete Frazier.',
    sent: 'October 2, 2026 10:23am',
  },
]

const GOALS = [
  { label: 'Fleet audit ready', value: 60, note: '6 of 10 documents current' },
  { label: 'Cost per mile under $1.85', value: 35, note: '$1.94 this month' },
]

export function TeamPanel({ width, comingSoon }: { width: number; comingSoon?: boolean }) {
  const agentName = useAgentName()

  return (
    <div
      style={{ width }}
      className={cn(
        'no-scrollbar border-ink-100 relative flex min-h-0 shrink-0 flex-col gap-5 overflow-y-auto border-l p-3',
        // Legible enough to show what it will hold, plainly not live yet.
        comingSoon && 'pointer-events-none'
      )}
    >
      {comingSoon && (
        <div className="absolute inset-0 z-10 flex justify-center pt-20">
          <Badge variant="secondary" className="h-fit text-[10px]">
            Coming soon
          </Badge>
        </div>
      )}
      <div className={cn('flex flex-col gap-5', comingSoon && 'opacity-40')}>
        <div className="flex flex-col gap-2">
          <Cap>Team tasks</Cap>
          {TASKS.map((task) => (
            <div key={task.label} className="flex items-start gap-2">
              <Checkbox checked={task.done} aria-label={task.label} className="mt-0.5 shrink-0" />
              <span className="text-ink min-w-0 flex-1 text-xs leading-snug">{task.label}</span>
              <span className="text-ink-400 shrink-0 text-[10px] leading-5">{task.owner}</span>
            </div>
          ))}
        </div>

        <div className="border-ink-100 flex flex-col gap-3 border-t pt-4">
          <Cap>{agentName} tasks</Cap>
          {BLUEY_TASKS.map((task) => (
            <div key={task.label} className="flex flex-col gap-0.5">
              {/* Opens what the agent has done so far, so the row has to read as a way in. */}
              <button
                type="button"
                className="hover:bg-ink-50 -mx-1 flex cursor-pointer items-start gap-1 rounded-md px-1 py-1 text-left"
              >
                <span className="text-ink flex-1 text-xs leading-snug">{task.label}</span>
                <ChevronRight className="text-ink-300 mt-0.5 size-3.5 shrink-0" />
              </button>
              <span className="text-ink-400 text-[10px] leading-snug">Last message sent, {task.sent}</span>
              <Button size="xs" variant="link" className="h-auto self-end px-0 text-[10px]">
                Cancel
              </Button>
            </div>
          ))}
        </div>

        <div className="border-ink-100 flex flex-col gap-3 border-t pt-4">
          <Cap>Goals</Cap>
          {GOALS.map((goal) => (
            <div key={goal.label} className="flex flex-col gap-1">
              <span className="text-ink text-xs">{goal.label}</span>
              <Progress value={goal.value} className="h-1.5" />
              <span className="text-ink-400 text-[10px]">{goal.note}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
